import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { nameBasedProtectedRoutes } from "router/protected";

import { Button, Card, Col, Row } from "react-bootstrap";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import moment from "moment";
import InputSelect from "../../components/common/InputSelect";
import InputDateRangePicker from "../../components/common/InputDateRangePicker";
import {
  datetimeFormats,
  invoiceStatusList,
  invoiceStatus,
} from "../../constants/constants";
import InputText from "components/common/InputText";
import {
  billsAndPaymentsApiCall,
  downloadInvoiceApiCall,
} from "services/invoice";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import BlackPinIcon from "assets/images/inspector-pin-icon.svg";
import ImageTag from "components/common/ImageTag";
import DownloadIocon from "assets/images/download-icon.svg";
import ViewIcon from "assets/images/view-icon.svg";
import { toast } from "react-toastify";
import Pagination from "@mui/material/Pagination";
import { GOOGLE_MAP_URL } from "constants/paths";

/**
 * Create a custom component which will render today's inspection section in dashboard.
 * @param props
 * @returns Today's Inspection component
 */
export const DataTable = (props: { userId: number }) => {
  localStorage.removeItem("inspection_machine_device_list")
  const PAGE_NAME = "bill_payment_list"
  const getSavedState = () => {
  const saved = localStorage.getItem(PAGE_NAME);
    return saved ? JSON.parse(saved) : {};
  };
  const savedState = getSavedState();
  let pageToFetch = savedState?.page || 0;
  if (!savedState?.page) {
    pageToFetch = 0;
  }

  let savedDate = null;
  if (savedState?.invoiceDate && savedState.invoiceDate.includes(",")) {
      const dateArr = savedState.invoiceDate.split(",");
      const startDate = moment(dateArr[0].trim());
      const endDate = moment(dateArr[1].trim());
      
      if (startDate.isValid() && endDate.isValid()) {
        savedDate = [startDate.toDate(), endDate.toDate()];
      }
  }
  
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  
  // 1. Initial State Constants
  const defaultStatus = state?.dueInvoice ? invoiceStatus.due : "all";
  const initialDateRange: [Date | null, Date | null] = null;

  const [sortColumn, setSortColumn] = useState(savedState?.sortColumn || "id");
  const [sortDirection, setSortDirection] = useState(savedState?.sortDirection || "desc");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort icon
  const [data, setData] = useState([]);
  const [search, setSearch] = useState(savedState?.search || "");
  const [dateRange, setDateRange] = useState<any>(savedDate);
  const [inspectionDate, setInspectionDate] = useState(savedState?.invoiceDate || "");
  const [status, setStatus] = useState(
    savedState?.status || (state?.dueInvoice ? invoiceStatus.due : "all")
  );
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [searchData, setSearchData] = useState({
    status: status,
    invoiceDate: inspectionDate,
    search: search,
  });
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: pageToFetch,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromRows, setFromRows] = useState(0);
  const [toRows, setToRows] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const formatter: any = new Intl.NumberFormat('en-US');
  const isFirstRun = useRef(true);

  
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchBillsAndPayments(); // Initial manual call
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchBillsAndPayments();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [sorting, search, dateRange, status, pagination.pageIndex, pagination.pageSize]); 

  /* useEffect(() => {
    fetchBillsAndPayments();
  }, [sorting, searchData, pagination.pageIndex, pagination.pageSize]); */
  
  const fetchBillsAndPayments = async () => {
    /* const param = {
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...searchData,
    }; */
    let formattedDate = "";
    if (dateRange && dateRange[0] && dateRange[1]) {
      const [startDate, endDate] = dateRange;
      const start = startDate ? moment(startDate).format("YYYY-MM-DD") : null;
      const end = endDate ? moment(endDate).format("YYYY-MM-DD") : null;
      formattedDate = `${start}, ${end}`;
    }
    const param = {
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection: 0 in sorting ? (sorting[0].desc ? "desc" : "asc") : sortDirection,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      status: status,
      invoiceDate: formattedDate,
      search: search,
    };
    const paramToSave: any = {...param};
    paramToSave['page'] = ((paramToSave['page'] - 1) < 0) ? 0 : paramToSave['page'] - 1
    localStorage.setItem(PAGE_NAME, JSON.stringify(paramToSave));
    billsAndPaymentsApiCall(param, props.userId).then((response) => {
      const newData = response.data.resultRows;
      setData(newData);
      setOutstandingBalance(response?.data?.outstandingBalance);
      setTotalPages(response.data.totalPages);
      setRowCount(response.data.totalResults);

      const fromRowsVal =
        newData.length > 0
          ? (response.data.page - 1) * response.data.limit + 1
          : 0;
      let toRowsVal = fromRowsVal + newData.length - 1;
      toRowsVal = toRowsVal > 0 ? toRowsVal : 0;
      setFromRows(fromRowsVal);
      setToRows(toRowsVal);
    });
  };

  const submitSearch = () => {
    const submitedData = {
      status: status,
      invoiceDate: inspectionDate,
      search: search,
    };
    const pagi = { ...pagination, pageIndex: 0 };
    setPagination(pagi);
    setSearchData(submitedData);
  };

  const inspectionDateFilter = (data: any) => {
    const dataRange = data;
    if (dataRange) {
      // since date range picker gives array containing start and end date, we are converting both the dates to YYYY-MM-DD format
      const startDate = moment(data[0]).format(datetimeFormats.databaseFormat);
      const endDate = moment(dataRange[1]).format(
        datetimeFormats.databaseFormat
      );
     // setInspectionDate(`${startDate},${endDate}`);
      setDateRange(data);
    } else {
     // setInspectionDate("");
      setDateRange(null);
    }
  };

  const searchInvoice = (event: any) => {
    setSearch(event.target.value);
    setPagination({ ...pagination, pageIndex: 0 });
  };

  const statusFilter = (event: any) => {
    setStatus(event.target.value);
    setPagination({ ...pagination, pageIndex: 0 });
  };

  const downloadInvoice = (id: number) => {
    downloadInvoiceApiCall({ id })
      .then((response) => {
        // console.log(response.data);
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const viewSR = (id: number) => {
    navigate(nameBasedProtectedRoutes.inspectionDetail.path + "/" + id);
  };

  /* Column Definition requires for MaterialReactTable */
  type BillsAndPayment = {
    id: number;
    invoice_number: string;
    amount: string;
    invoice_date: string;
    days_overdue: string;
    status: string;
    Inspection: {
      id: number;
      po_no: string;
      address: string;
      city: string;
      zipcode: string;
      inspection_date: string;
      job_time: string;
      no_machines: string;
      state: {
        title: string;
      };
    };
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<BillsAndPayment>[]>(
    () => [
      {
        accessorKey: "invoice_number",
        header: "INVOICE NO.",
        muiTableHeadCellProps: () => ({ className: "text-nowrap" }),
        Cell: ({ cell, row }) => `#${row.original.invoice_number}`,
      },
      {
        accessorKey: "invoice_date",
        header: "PAYMENT DATE",
        muiTableHeadCellProps: () => ({ className: "text-nowrap" }),
        Cell: ({ cell, row }) => {
          if (row.original.status === invoiceStatus.paid) {
            return row.original?.invoice_date
              ? moment(`${row.original?.invoice_date} `).format(
                  datetimeFormats.separatedDate
                )
              : "-";
          }
          return "-";
        },
      },
      {
        accessorKey: "amount",
        header: "AMOUNT",
        //Cell: ({ cell, row }) => `$${row.original.amount}`,
        Cell: ({ cell, row }) => `$${row?.original?.amount ? formatter.format(row.original.amount) : 0}`,
      },
      {
        accessorKey: "days_overdue",
        header: "DAYS PAST DUE",
        muiTableHeadCellProps: () => ({ className: "past-due-data" }),
        Cell: ({ cell, row }) => {
          let over_due = row.original?.days_overdue
            ? row.original.days_overdue
            : 0;
          over_due = over_due > 0 ? over_due : 0;
          return row.original.status === invoiceStatus.paid ? "-" : over_due;
        },
      },
      {
        accessorKey: "Inspection.po_no",
        header: "PO NUMBER",
        muiTableHeadCellProps: () => ({ className: "past-due-data" }),
      },
      {
        accessorKey: "Inspection.address",
        header: "LOCATION",
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const inspectionData = row.original.Inspection;
          const city = inspectionData?.city ? ", " + inspectionData?.city : "";
          const state = inspectionData?.state
            ? ", " + inspectionData?.state?.title
            : "";
          const zip = inspectionData?.zipcode
            ? ", " + inspectionData?.zipcode
            : "";
          const fullAddress = inspectionData.address + city + state + zip;
          return inspectionData.address ? (
            <OverlayTrigger overlay={<Tooltip>{fullAddress}</Tooltip>}>
              <span role="button" className="d-inline-block">
                <Link target="_blank" to={`${GOOGLE_MAP_URL}${fullAddress}`}>
                  <ImageTag src={BlackPinIcon} />
                </Link>
              </span>
            </OverlayTrigger>
          ) : (
            ""
          );
        },
      },
      {
        accessorKey: "Inspection.no_machines",
        header: "NO. OF MACHINES",
      },
      {
        accessorKey: "Inspection.job_time",
        header: "INSPECTION DATE",
        muiTableHeadCellProps: () => ({ className: "text-nowrap" }),
        Cell: ({ cell, row }) => {
          return row.original?.Inspection?.inspection_date
            ? moment(
                `${row.original?.Inspection?.inspection_date} ${row.original?.Inspection?.job_time}`
              ).format(datetimeFormats.separatedDateTime)
            : "-";
        },
      },
      {
        accessorKey: "status",
        header: "STATUS",
        Cell: ({ cell, row }) => {
          switch (row.original?.status) {
            case invoiceStatus.due:
              return (
                <span className="text-danger bg-light-yellow fs-15  p-1 pe-2 ps-2 text-light-yellow cap-text border-radius-5">
                  {invoiceStatus.due}
                </span>
              );
            case invoiceStatus.paid:
              return (
                <span className="text-success bg-ligt-green fs-15 p-1 pe-2 ps-2 text-green cap-text border-radius-5">
                  {invoiceStatus.paid}
                </span>
              );
            default:
              return row.original?.status?.toUpperCase();
          }
        },
      },
      {
        accessorKey: "id",
        header: "ACTION",
        enableSorting: false,
        Cell: ({ cell, row }) => {
          return (
            <>
              <OverlayTrigger overlay={<Tooltip>Download Invoice</Tooltip>}>
                <span
                  role="button"
                  onClick={() => downloadInvoice(row.original?.id)}
                  className="d-inline-block"
                >
                  <ImageTag src={DownloadIocon} alt="Download Invoice" />
                </span>
              </OverlayTrigger>{" "}
              <OverlayTrigger overlay={<Tooltip>View SR</Tooltip>}>
                <span
                  onClick={() => viewSR(row.original?.Inspection?.id)}
                  role="button"
                  className="d-inline-block"
                >
                  <ImageTag src={ViewIcon} alt="View SR" />
                </span>
              </OverlayTrigger>
            </>
          );
        },
      },
    ],
    []
  );

  const today = new Date();
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const formattedDate: string = today.toLocaleDateString("en-US", options);

  const handleReset = () => {
    setSearch("");
    setStatus(defaultStatus);
    setDateRange(null);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setResetKey(prev => prev + 1);
  };

  return (
    <div className="col-xl-12 mb-3">
      <Card.Body className="br-10 p-4 new-table-design">
        <Row className="">
          <Col className="billing-serch-input col-md-12 col-lg-3  col-12 col">
            <InputText
              controlId="search"
              label=""
              placeholder="Search by Invoice No./Amount"
              touched={false}
              handleBlur={false}
              handleChange={searchInvoice}
              errorsField={false}
              value={search}
            />
          </Col>
          <Col className="col-md-12 col-lg-9 col-12 col">
            <Row className="float-end">
              <Col className="fs-13 text-end col-auto seprate-line">
                Outstanding Balance <br />
                <span className="fs-18 fw-bold billing-yellow-text">
                  {outstandingBalance !== undefined && outstandingBalance !== null
                    ? new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(outstandingBalance)
                    : "-"}
                </span>
              </Col>
              <Col className="inspector-date-icon  dashboard-input-cover">
                <InputDateRangePicker
                  controlId="inspection_date"
                  label="Inspection Date"
                  placeholder="Date Range"
                  touched={false}
                  handleBlur={false}
                  handleChange={inspectionDateFilter}
                  handleClose={inspectionDateFilter}
                  errorsField={false}
                  value={dateRange}
                  position="bottomEnd"
                  key={`datepicker-${resetKey}`}
                />
              </Col>
              <Col className="dashboard-input-cover">
                <InputSelect
                  controlId="status"
                  label="Status"
                  placeholder="Status"
                  touched={false}
                  handleBlur={false}
                  handleChange={statusFilter}
                  errorsField={false}
                  value={status}
                  options={[
                    { key: "all", value: "All Status" },
                    ...invoiceStatusList,
                  ]}
                  key={JSON.stringify(status)}
                />
              </Col>
              <Col className="">
                <Button onClick={handleReset} variant="danger" className="fw-semibold">
                  Reset
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="table-responsive ">
          <MaterialReactTable
            muiTableProps={{
              classes: {
                root: "table custom-table border-none-table table-striped machine-custome-table bills-table",
              },
            }}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableHiding={false}
            enableColumnFilters={false}
            enablePagination={false}
            columns={tableColumns}
            data={data}
            onSortingChange={setSorting}
            manualSorting
            state={{ sorting }}
          />
          {totalPages > 0 ? (
            <div className="paggination">
              <p className="showing-text">
                Showing {fromRows}-{toRows} of {rowCount}
              </p>
              <Pagination
                count={totalPages}
                page={pagination.pageIndex + 1}
                shape="rounded"
                variant="outlined"
                onChange={(event, newPage) => {
                  const pagi = { ...pagination, pageIndex: newPage - 1 };
                  setPagination(pagi);
                }}
              />
            </div>
          ) : (
            ""
          )}
        </div>
      </Card.Body>
    </div>
  );
};
